import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex justify-center mb-8">
            <Logo />
          </div>

          <h1 className="text-3xl font-bold mb-8 text-center">Terms of Service</h1>
          
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Service Description</h2>
              <p className="text-gray-600">
                SmartiStat provides an automated workflow solution for Sterile Processing Departments (SPD) and Operating Rooms (OR) in healthcare facilities. Our platform streamlines documentation, communication, and workflow processes to enhance patient safety and operational efficiency.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing or using the SmartiStat platform, you agree to be bound by these Terms of Service. If you are using the service on behalf of an organization, you represent that you have authority to bind that organization to these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Subscription and Payment</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Access to SmartiStat requires a valid subscription.</li>
                <li>Fees are based on service level and facility size as detailed in your service agreement.</li>
                <li>Invoices are issued monthly or annually according to the payment schedule in your agreement.</li>
                <li>Payment is due within 30 days of invoice date unless otherwise specified.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Data Ownership and Usage</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>All customer data entered into the system remains the property of the customer.</li>
                <li>SmartiStat may use anonymized, aggregated data for service improvements and analytics.</li>
                <li>We will not access your data except for service delivery, system maintenance, or as required by law.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Healthcare Compliance</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Our platform is designed to help facilities maintain compliance with relevant healthcare regulations.</li>
                <li>Customers remain responsible for ensuring their use of the system meets regulatory requirements.</li>
                <li>SmartiStat is not responsible for regulatory penalties resulting from improper system use.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Service Level and Support</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>SmartiStat guarantees 99.9% uptime excluding scheduled maintenance.</li>
                <li>Technical support is available during business hours with emergency support available 24/7.</li>
                <li>Maintenance windows will be scheduled with advance notice except in emergencies.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Term and Termination</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Initial term is as specified in your service agreement.</li>
                <li>Either party may terminate for material breach with 30 days written notice.</li>
                <li>Upon termination, customer data will be available for export for 30 days.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Limitation of Liability</h2>
              <p className="text-gray-600">
                SmartiStat's liability shall not exceed the amount paid by customer in the 12 months preceding any claim.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Updates to Terms</h2>
              <p className="text-gray-600">
                These terms may be updated periodically. Continued use after notification constitutes acceptance of updated terms.
              </p>
            </section>

            <div className="text-sm text-gray-500 pt-8 border-t">
              Last Updated: May 4, 2025
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;